import { Suspense, lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrderItem } from "@/api/types";
import { DxfPreview } from "@/components/DxfPreview";
import { meshFormatFrom } from "@/lib/meshFormat";
import { formatOrderLineDimensions } from "@/lib/orderDisplay";
import { cx, formatMoney } from "@/lib/utils";

const MeshPreview = lazy(() => import("@/components/MeshPreview"));

interface OrderItemRowProps {
  item: OrderItem;
  locale: string;
  // Order's transaction currency + frozen CZK rate (Model A). Defaults keep
  // CZK callers (e.g. admin detail) byte-identical.
  currency?: string;
  fxRate?: number;
}

// Per-row dimensions blurb — universal across processes, rendered by
// the shared lib/dimensionDisplay formatter (WS 3D). PR15 dropped the
// legacy flat-axis fallback — BE mapper guarantees dimensions is
// populated (either dual-write from cart or fallback
// the folded legacy axis pair from the router).

/**
 * Item row na thank-you stránce: thumbnail vlevo + popis (file_name +
 * lines) vpravo. Klik na thumbnail (nebo file_name) otevře modal
 * s plným pan/zoom DxfPreview, resp. orbit MeshPreview pro mesh uploady.
 *
 * Thumbnail i modal čtou stejný presigned URL (``svg_preview_url`` /
 * ``mesh_preview_url``) z ``OrderItemView`` — žádný extra request.
 */
export function OrderItemRow({
  item,
  locale,
  currency = "CZK",
  fxRate = 1,
}: OrderItemRowProps): JSX.Element {
  const { t } = useTranslation("order");
  const [open, setOpen] = useState(false);
  // Which preview an item gets is the module's declared capability —
  // the API presigns exactly one of the two URLs, so the presence of the
  // URL is the branch. (Reading the file extension here would drop the
  // preview for every mesh format that isn't .stl.)
  const hasSvg = Boolean(item.svg_preview_url);
  const hasMesh = Boolean(item.mesh_preview_url);
  const hasPreview = hasSvg || hasMesh;

  return (
    <li className="flex gap-4 p-4">
      <Thumbnail item={item} onClick={hasPreview ? () => setOpen(true) : undefined} />
      <div className="min-w-0 flex-1">
        {hasPreview ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-left font-mono text-sm text-ink hover:underline"
            data-testid="order-item-open-preview"
            aria-label={t("itemPreview.open")}
          >
            {item.file_name}
          </button>
        ) : (
          <div className="font-mono text-sm text-ink">{item.file_name}</div>
        )}
        <ul className="mt-2 space-y-1 text-sm text-ink-2">
          {item.lines.map((line, j) => (
            <li key={j}>
              {line.material_display_name || line.material_code} ·{" "}
              {formatOrderLineDimensions(line, t("lineModifier"), { locale })} ·{" "}
              {line.quantity}×{" "}
              {line.undelivered_qty ? (
                <span className="text-warn" data-testid="order-line-undelivered">
                  {t("lineUndelivered", {
                    defaultValue: "(nedodáno {{count}})",
                    count: line.undelivered_qty,
                  })}{" "}
                </span>
              ) : null}
              <span className="float-right tabular-nums text-ink">
                {/* KAN-2099 — cena výroby; příplatek za termín má vlastní
                    řádek pod položkami. */}
                {formatMoney(
                  line.production_line_total_major ?? line.line_total_czk,
                  currency,
                  locale,
                  fxRate,
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {open && hasPreview && (
        <PreviewModal
          item={item}
          hasSvg={hasSvg}
          hasMesh={hasMesh}
          onClose={() => setOpen(false)}
        />
      )}
    </li>
  );
}

const THUMB_BOX =
  "h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-border bg-bg";

function Thumbnail({
  item,
  onClick,
}: {
  item: OrderItem;
  onClick?: () => void;
}): JSX.Element {
  const lower = item.file_name.toLowerCase();

  // Vektorový náhled — statický <img> ze SVG presign URL. Žádný pan/zoom
  // v thumbnailu, ten dostane uživatel až v modalu.
  let inner: JSX.Element;
  if (item.svg_preview_url) {
    inner = (
      <img
        src={item.svg_preview_url}
        alt=""
        className="h-full w-full object-contain"
        loading="lazy"
      />
    );
  } else if (item.mesh_preview_url) {
    // Mountovat three.js v thumbnailu by byl overkill — render placeholder
    // s badge formátu a plný viewer ukážeme až v modalu po otevření.
    // (Admin AdminOrderDetail mountuje mini three.js pomocí
    // IntersectionObserver, ale zákaznická thank-you stránka má jednu
    // sekci a thumbnaily jsou cestou jen vizuální vodítko.)
    inner = (
      <div className="flex h-full w-full items-center justify-center bg-custom-bg text-[10px] font-semibold uppercase tracking-wide text-custom">
        {meshFormatFrom(item.file_name)}
      </div>
    );
  } else {
    const ext = lower.split(".").pop()?.slice(0, 4) ?? "file";
    inner = (
      <div className="flex h-full w-full items-center justify-center font-mono text-[10px] uppercase text-ink-3">
        {ext}
      </div>
    );
  }

  if (!onClick) {
    return (
      <div className={THUMB_BOX} aria-hidden="true">
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(THUMB_BOX, "cursor-zoom-in transition hover:border-ink")}
      data-testid="order-item-thumbnail"
    >
      {inner}
    </button>
  );
}

function PreviewModal({
  item,
  hasSvg,
  hasMesh,
  onClose,
}: {
  item: OrderItem;
  hasSvg: boolean;
  hasMesh: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation("order");

  // Esc — zavřít. Capture na document, ne na modal divu, abychom ho
  // chytli i když fokus ujel mimo modal (např. na <img>).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="order-item-preview-modal"
      role="dialog"
      aria-modal="true"
      aria-label={item.file_name}
    >
      <div
        className="relative flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-surface shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-2">
          <span className="truncate font-mono text-sm text-ink">
            {item.file_name}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-3 transition hover:bg-bg hover:text-ink"
            aria-label={t("itemPreview.close")}
            data-testid="order-item-preview-close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>
        <div className="flex min-h-0 flex-1 items-stretch justify-stretch p-3">
          {hasSvg && item.svg_preview_url ? (
            <DxfPreview
              src={item.svg_preview_url}
              alt={item.file_name}
              fileName={item.file_name}
              chrome={true}
              className="h-full w-full"
            />
          ) : hasMesh && item.mesh_preview_url ? (
            <Suspense fallback={null}>
              <MeshPreview
                src={item.mesh_preview_url}
                rotation={{ x: 0, y: 0, z: 0 }}
                fileName={item.file_name}
              />
            </Suspense>
          ) : null}
        </div>
      </div>
    </div>
  );
}
